import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { webStorage } from 'sidecar-utilities'

import { useSidekick } from 'hooks/useSidekick'
import { chooseBestUserLocation } from 'utils/chooseBestUserLocation'
import { getAshPage } from './getAshPage'

/**
 * @param {string} authToken - The user's authentication token to be given to
 *   the Sidekick app so it doesn't need a separate login.
 * @param {Object} authedUser - The authenticated user object containing profile
 *   information
 * @param {number} lat - The latitude of the user's location.
 * @param {number} lon - The longitude of the user's location.
 * @param {string} zip - The zip code of the user's location.
 * @returns {JSX.Element} An iframe that loads the Sidekick AI assistant.
 */
export const BaseSidekick = ({
  authToken,
  authedUser,
  currentLocation,
  ipLocation,
}) => {
  const { setConfig, sourceUrl, isChatReady, resetChatReady } = useSidekick()
  const { pathname } = useLocation()
  const flags = useFlags()

  // Given all the various locations we know about, we want to get
  // the best, most relevant one and give that to the LLM for context.
  const { lat, lon, zip } =
    chooseBestUserLocation({
      authedUser,
      currentLocation,
      ipLocation,
    }) || {}

  // Feature flag to control showing the initial message with start
  // questions when the SideKick first opens.
  const showStarterQuestions = flags['sidekick-starters']
  const isAshChatGate = flags['ash-chat-gate']
  // Picks whether Ash opens with questions about the expense on screen.
  const isAshPageContextEnabled = !!flags['ash-page-context-enabled']
  const context = pathname.startsWith('/expenses') ? 'expense' : undefined
  // What's on screen. The poptart is non-modal, so the member can navigate with
  // Ash open, and the effect below re-sends whenever this changes.
  const page = useMemo(() => getAshPage(pathname), [pathname])

  // Reset chat ready state when this component unmounts, since each iframe
  // instance needs to send CHAT_READY when it loads
  useEffect(() => {
    return () => {
      resetChatReady()
    }
  }, [resetChatReady])

  // When this page first loads, we need to send a bunch of info
  // to the Sidekick app. Optionally, we could also append the
  // token to the URL as `?token=` and it would do the same thing.
  useEffect(() => {
    // If we don't have an auth token or the chat isn't ready,
    // we don't need to do anything yet.
    if (!authToken || !isChatReady) return

    // Get the first letter of the user's first name to use as
    // the initial in the Sidekick message bubble.
    const userInitial = authedUser?.firstName?.charAt(0) || ''
    const productType = authedUser?.policy?.insuranceProduct || ''
    setConfig({
      accountUuid: webStorage.getItem('aud'),
      authToken,
      context,
      isAshChatGate,
      isAshPageContextEnabled,
      lat,
      lon,
      memberUuid: authedUser?.uuid,
      // Always sent, `null` off an expense page, so Ash never stays on the last one.
      page,
      productType,
      showStarterQuestions,
      userInitial,
      zip,
    })
  }, [
    authToken,
    authedUser,
    currentLocation,
    isAshChatGate,
    isAshPageContextEnabled,
    lat,
    lon,
    isChatReady,
    context,
    page,
    setConfig,
    showStarterQuestions,
    zip,
  ])

  return (
    // `flex-1 min-h-0` rather than `h-full`: the poptart body's own height is a
    // specified value that flex shrinks, and WebKit resolves a percentage height
    // against the specified one, which left this iframe taller than the box holding
    // it and Ash's composer below a scroll. Needs the column set by
    // `SidekickPoptart`'s wrapper; `min-h-0` keeps flex's automatic minimum from
    // holding the iframe at its content height.
    <iframe
      id="shSidekick"
      className="w-full flex-1 min-h-0"
      src={sourceUrl}
      title="Ash AI Assistant"
    />
  )
}

const mapStateToProps = ({ Auth, Location }) => ({
  authToken: Auth?.authToken || null,
  authedUser: Auth?.authedUser || null,
  ipLocation: Location?.ipLocation?.entity || null,
  currentLocation: Location?.currentLocation?.entity || null,
})

export const Sidekick = connect(mapStateToProps, null)(BaseSidekick)
